<template>
  <AuthLayout
    data-testid="login"
    :loading="cardLoading"
    :disabled="cardDisabled"
    @ready="onAuthLayoutReady"
  >
    <div v-if="alreadylogin && !$store.state.loading">
      <loader />
    </div>

    <div v-else-if="logintype === 'okta'" class="login-panel-body" data-testid="login-okta">
      <AuthLoginLogo />
      <header class="login-okta__header">
        <h1 class="login-okta__title">{{ AUTH.SIGN_IN_HEADING }}</h1>
        <p class="login-okta__subtitle">{{ AUTH.DISCOVER_SUBTITLE }}</p>
      </header>
      <v-btn
        class="login-okta__submit"
        color="primary"
        block
        large
        elevation="0"
        @click="oktaLogin()"
        data-testid="login-okta__submit"
      >
        <span>Login using Okta</span>
      </v-btn>
    </div>

    <div v-else-if="logintype === 'sso'" class="login-panel-body" data-testid="login-sso">
      <AuthLoginLogo />
      <SsoHandoff
        v-if="ssoInProgress"
        :display-name="ssoHandoffDisplayName"
        :alert-message="displayMsg"
        @cancel="onSsoHandoffCancel"
      />
      <DiscoverEmailForm
        v-else-if="loginStep === AUTH.LOGIN_STEPS.DISCOVER"
        :email="discoverEmail"
        :loading="ssoLoading"
        :alert-message="displayMsg"
        @continue="onDiscoverContinue"
      />
      <WorkspacePicker
        v-else-if="loginStep === AUTH.LOGIN_STEPS.CHOOSE"
        :email="discoverEmail"
        :tenants="discoverTenants"
        :alert-message="displayMsg"
        @select="onWorkspaceSelect"
        @back="onBackToDiscover"
      />
      <TenantLoginForm
        v-else-if="loginStep === AUTH.LOGIN_STEPS.CREDENTIALS"
        ref="tenantLogin"
        :email="discoverEmail || prefillEmail"
        :local-allowed="credentialsLocalAllowed"
        :idps="credentialsIdps"
        :loading="ssoLoading"
        :sso-in-progress="ssoInProgress"
        :show-back="isAppHostMode"
        :alert-message="displayMsg"
        @password-login="onPasswordLogin"
        @sso-start="onSsoStart"
        @back="onBackToDiscover"
      />
    </div>

    <div v-else-if="logintype === 'normal'" class="login-panel-body" data-testid="login-normal">
      <AuthLoginLogo />
      <TenantLoginForm
        ref="normalLogin"
        :email="username || prefillEmail"
        :local-allowed="true"
        :idps="emptyIdps"
        :loading="loading"
        :sso-in-progress="false"
        :show-back="false"
        :alert-message="displayMsg"
        @password-login="onNormalPasswordLogin"
      />
    </div>

    <div v-else>
      <AuthLoginLogo />
      <AuthLoginAlert :message="displayMsg" />
    </div>
  </AuthLayout>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import loader from '../common/Loader';
import DiscoverEmailForm from './components/DiscoverEmailForm.vue';
import WorkspacePicker from './components/WorkspacePicker.vue';
import TenantLoginForm from './components/TenantLoginForm.vue';
import { getPasswordLoginErrorMessage } from './utils/authLoginUtils';
import AuthLayout from './components/AuthLayout.vue';
import AuthLoginLogo from './components/AuthLoginLogo.vue';
import AuthLoginAlert from './components/AuthLoginAlert.vue';
import SsoHandoff from './components/SsoHandoff.vue';
import { isAuthBffConfigured } from '../../plugins/authBff';
import {
  isLoopbackHostname,
  isSharedHostingMode,
} from '../../plugins/hostMode';
import { getApiUrl } from '../../plugins/serviceUrls';
import constants from '../../plugins/constants';

const { AUTH } = constants;

export default {
  name: 'login',
  components: {
    loader,
    DiscoverEmailForm,
    WorkspacePicker,
    TenantLoginForm,
    AuthLayout,
    AuthLoginLogo,
    AuthLoginAlert,
    SsoHandoff,
  },
  data: () => ({
    username: '',
    password: '',
    loading: false,
    alreadylogin: true,
    logintype: '',
    msg: '',
    prefillEmail: '',
    activeSsoIdp: null,
    emptyIdps: [],
    AUTH,
    authLayoutReady: false,
    pendingBootDismiss: false,
  }),
  computed: {
    ...mapState('auth', [
      'loginStep',
      'discoverEmail',
      'discoverTenants',
      'errorMessage',
      'ssoInProgress',
      'hostMode',
    ]),
    ...mapGetters('auth', [
      'localAllowed',
      'idps',
      'authPolicy',
      'isAppHost',
      'isDedicatedHost',
    ]),
    credentialsLocalAllowed() {
      const policy = this.authPolicy;
      if (policy === AUTH.AUTH_POLICIES.SSO_ONLY) return false;
      if (policy === AUTH.AUTH_POLICIES.SSO_WITH_BREAK_GLASS) {
        return Boolean(this.prefillEmail);
      }
      if (
        policy === AUTH.AUTH_POLICIES.LOCAL_ONLY ||
        policy === AUTH.AUTH_POLICIES.HYBRID
      ) {
        return true;
      }
      return this.localAllowed;
    },
    credentialsIdps() {
      if (this.authPolicy === AUTH.AUTH_POLICIES.LOCAL_ONLY) return this.emptyIdps;
      return this.idps;
    },
    authBusy() {
      return this.$store.state.auth.loading;
    },
    ssoLoading() {
      return this.authBusy || this.ssoInProgress;
    },
    cardLoading() {
      if (this.logintype === 'sso') {
        return this.ssoLoading && !this.ssoInProgress;
      }
      return this.loading;
    },
    cardDisabled() {
      if (this.logintype === 'sso') {
        return this.ssoLoading && !this.ssoInProgress;
      }
      return this.loading;
    },
    displayMsg() {
      return this.errorMessage || this.msg || '';
    },
    isAppHostMode() {
      return this.isAppHost;
    },
    ssoHandoffDisplayName() {
      return (
        (this.activeSsoIdp && this.activeSsoIdp.displayName) ||
        (this.idps && this.idps[0] && this.idps[0].displayName) ||
        ''
      );
    },
  },
  created() {
    try {
      if (sessionStorage.getItem('auth_client_mismatch')) {
        sessionStorage.removeItem('auth_client_mismatch');
        console.warn({ message: 'Client mismatch', meta: {} });
        this.msg =
          constants.AUTH.CLIENT_MISMATCH_MESSAGE ||
          'Your workspace selection no longer matches this session. Please sign in again.';
      } else if (sessionStorage.getItem('session_expired')) {
        sessionStorage.removeItem('session_expired');
        console.warn({ message: 'Session expired', meta: {} });
        this.msg = 'Your session has expired. Please log in again.';
      }
    } catch (_) { /* storage unavailable */ }

    this.prefillEmail = String(this.$route.query.email || '').trim().toLowerCase();

    if (this.$store.state.login) {
      this.alreadylogin = false;
      return;
    }

    if (this.$route.query.token) this.verifyToken();
    else this.checkLogin();
  },
  mounted() {
    window.addEventListener('keydown', this.handleEnterKey);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleEnterKey);
    this.username = '';
    this.password = '';
  },
  methods: {
    onAuthLayoutReady() {
      this.authLayoutReady = true;
      this.tryDismissBootLoading();
    },
    tryDismissBootLoading() {
      if (!this.pendingBootDismiss || !this.authLayoutReady) return;
      this.pendingBootDismiss = false;
      if (this.$store.state.loading > 0) {
        this.$store.commit('removeLoading');
      }
    },
    handleEnterKey(event) {
      if (event.key !== 'Enter') return;
      if (this.logintype === 'normal') return;
      if (this.logintype !== 'sso') return;
      if (this.loginStep === AUTH.LOGIN_STEPS.DISCOVER && this.discoverEmail) {
        this.onDiscoverContinue(this.discoverEmail);
      } else if (this.loginStep === AUTH.LOGIN_STEPS.CREDENTIALS && this.credentialsLocalAllowed) {
        const form = this.$refs.tenantLogin;
        if (form && form.getCredentials) {
          const creds = form.getCredentials();
          if (creds.email && creds.password) this.onPasswordLogin(creds);
        }
      }
    },
    async onNormalPasswordLogin({ email, password }) {
      this.username = email;
      this.password = password;
      this.msg = '';
      this.loading = true;
      try {
        const result = await this.$store.dispatch('auth/localLogin', { email, password });
        if (result) {
          this.checkLogin({ skipBootstrap: true });
        }
      } finally {
        this.loading = false;
      }
    },
    storeAuthTokens(response) {
      this.$store.dispatch('auth/setAuthFromResponse', response);
    },
    resolveUnauthenticatedLoginType() {
      let loginType = 'normal';
      if (process.env.VUE_APP_AUTH_TYPE === 'okta') {
        loginType = 'okta';
      } else if (isAuthBffConfigured()) {
        loginType = 'sso';
      }
      console.info({ message: 'Login type chosen', meta: { loginType } });
      return loginType;
    },
    async bootstrapSsoLogin() {
      await this.$store.dispatch('auth/initHostMode');
      this.logintype = 'sso';

      const queryClient = String(this.$route.query.client || '').trim();
      if (this.prefillEmail) {
        this.$store.commit('auth/setDiscoverEmail', this.prefillEmail);
      }

      const hostname =
        typeof window !== 'undefined' ? window.location.hostname : '';
      const loopback = isLoopbackHostname(hostname);

      // App host: optional deep-link ?client= skips discover when present
      if (queryClient) {
        this.$store.commit('auth/setSelectedDiscoverClient', queryClient);
        this.$store.commit('auth/setLoginStep', AUTH.LOGIN_STEPS.CREDENTIALS);
        await this.$store.dispatch('auth/loadLoginOptions', { client: queryClient });
        return;
      }

      // Hosted: Auth BFF domain row (hostname → hostingMode) is source of truth.
      // Env VUE_APP_APP_HOST mismatch / UNKNOWN must not skip discover on SHARED hosts.
      // Loopback L1 stays APP_HOST without this probe.
      if (!loopback) {
        const options = await this.$store.dispatch('auth/loadLoginOptions', {});
        if (isSharedHostingMode(options?.hostingMode, options?.isAppHost)) {
          console.info({ message: 'Hosted bootstrap', meta: { hosting: 'SHARED' } });
          this.$store.commit('auth/setHostMode', AUTH.HOST_MODES.APP_HOST);
          this.$store.commit('auth/setLoginOptions', null);
          this.$store.commit('auth/setSelectedDiscoverClient', null);
          this.$store.commit('auth/setLoginStep', AUTH.LOGIN_STEPS.DISCOVER);
          return;
        }
        if (options) {
          console.info({ message: 'Hosted bootstrap', meta: { hosting: 'DEDICATED' } });
          this.$store.commit('auth/setHostMode', AUTH.HOST_MODES.DEDICATED);
          this.$store.commit('auth/setLoginStep', AUTH.LOGIN_STEPS.CREDENTIALS);
          return;
        }
      }

      if (
        this.isDedicatedHost ||
        this.hostMode === AUTH.HOST_MODES.DEDICATED ||
        this.hostMode === AUTH.HOST_MODES.UNKNOWN
      ) {
        if (!loopback) {
          console.info({
            message: 'Hosted bootstrap',
            meta: {
              hosting:
                this.hostMode === AUTH.HOST_MODES.APP_HOST ? 'SHARED' : 'DEDICATED',
            },
          });
        }
        this.$store.commit('auth/setLoginStep', AUTH.LOGIN_STEPS.CREDENTIALS);
        await this.$store.dispatch('auth/loadLoginOptions', {
          client: queryClient || undefined,
        });
        return;
      }

      this.$store.commit('auth/setLoginStep', AUTH.LOGIN_STEPS.DISCOVER);
    },
    async onDiscoverContinue(email) {
      this.msg = '';
      await this.$store.dispatch('auth/discover', email);
    },
    async onWorkspaceSelect(tenant) {
      await this.$store.dispatch('auth/selectDiscoverTenant', tenant);
    },
    onBackToDiscover() {
      this.activeSsoIdp = null;
      this.$store.commit('auth/setLoginOptions', null);
      this.$store.commit('auth/setSelectedDiscoverClient', null);
      this.$store.commit('auth/setLoginStep', AUTH.LOGIN_STEPS.DISCOVER);
      this.$store.commit('auth/setErrorMessage', '');
    },
    async onPasswordLogin({ email, password }) {
      this.msg = '';
      const result = await this.$store.dispatch('auth/localLogin', { email, password });
      if (result) {
        // Re-run checklogin to hydrate full user/session parity with legacy path
        this.checkLogin({ skipBootstrap: true });
      }
    },
    onSsoHandoffCancel() {
      this.$store.commit('auth/setSsoInProgress', false);
    },
    async onSsoStart(idp) {
      if (!idp || !idp.connectionId) {
        return;
      }
      this.activeSsoIdp = idp;
      let returnPath = '/';
      try {
        returnPath =
          sessionStorage.getItem(constants.STORAGE_KEYS.POST_LOGIN_RETURN_PATH) || '/';
      } catch (_) { /* storage unavailable */ }
      await this.$store.dispatch('auth/startSso', {
        connectionId: idp.connectionId,
        returnPath,
      });
    },
    verifyToken() {
      this.loading = true;
      let shouldDismissAppLoading = false;
      this.axios
        .post('/v2/auth/okta/verifycallback', {})
        .then((x) => {
          if (x.data.status == 'success') {
            this.storeAuthTokens(x);
            this.$store.commit('setUser', x.data.data);
            this.$store.commit('changeLogin', true);
            this.msg = '';
            let routeData = {
              path: this.$route.path,
              query: { ...this.$route.query.cid },
              hash: this.$route.hash,
            };
            this.$router.push(routeData);
          } else {
            shouldDismissAppLoading = true;
            this.msg = x.data.message || "unknown error";
            this.$store.commit("changeLogin", false);
            this.alreadylogin = false;
            this.logintype = this.resolveUnauthenticatedLoginType();
            if (this.logintype === 'sso') this.bootstrapSsoLogin();
          }
        }).catch(() => {
          shouldDismissAppLoading = true;
          this.msg = "cannot connect to server";
          this.alreadylogin = false;
          if(process.env.VUE_APP_AUTH_TYPE === 'okta') this.logintype = 'okta';
          else this.logintype = 'normal';
        }).finally(() => {
          if (shouldDismissAppLoading) {
            this.pendingBootDismiss = true;
            this.tryDismissBootLoading();
          }
          this.loading = false;
        });
    },
    checkLogin({ skipBootstrap = false } = {}) {
      this.loading = true;
      let shouldDismissAppLoading = false;

      this.axios
        .post('/v2/auth/checklogin', {})
        .then(async (x) => {
          if (x.data.status == 'success') {
            await this.$store.dispatch('auth/applyAuthSuccess', { response: x });
            this.msg = '';
          } else {
            shouldDismissAppLoading = true;
            if (
              x.data.message !== 'Not logged in' &&
              x.data.message !== 'Login expired'
            ) {
              this.msg = x.data.message || 'unknown error';
            }
            this.$store.commit('changeLogin', false);
            this.alreadylogin = false;
            this.logintype = this.resolveUnauthenticatedLoginType();
            if (!skipBootstrap && this.logintype === 'sso') {
              await this.bootstrapSsoLogin();
            }
          }
        }).catch(() => {
          shouldDismissAppLoading = true;
          this.msg = "cannot connect to server";
          this.alreadylogin = false;
          if(process.env.VUE_APP_AUTH_TYPE === 'okta') this.logintype = 'okta';
          else this.logintype = 'normal';
        }).finally(() => {
          if (shouldDismissAppLoading) {
            this.pendingBootDismiss = true;
            this.tryDismissBootLoading();
          }
          this.loading = false;
          if (localStorage.getItem('commentary_custom_filter')) {
            localStorage.setItem('commentary_custom_filter', '');
            localStorage.setItem('commentary_custom_filter_toggle', false);
          }
        });
    },
    submitLogin() {
      this.loading = true;
      this.axios
        .post('/v2/auth/local', {
          username: this.username,
          password: this.password,
        })
        .then(async (x) => {
          if (x.data.status == 'success') {
            await this.$store.dispatch('auth/applyAuthSuccess', { response: x });
            this.checkLogin({ skipBootstrap: true });
          } else {
            this.msg = getPasswordLoginErrorMessage();
          }
        })
        .catch(() => {
          this.msg = getPasswordLoginErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    oktaLogin() {
      window.location.href = getApiUrl() + '/v2/auth/okta';
    },
  },
};
</script>
<style lang="scss" scoped>
@import './Login.scss';
</style>
